import React, { useEffect, useRef, useState } from "react";
import ErrorMessage from "../../../shared/custom-components/errorMessage";
import Table from "canvas-core-react/lib/Table";
import SecondaryButton from "canvas-core-react/lib/SecondaryButton";
import { useTranslation } from "react-i18next";

import Checkbox from "../../../shared/custom-components/checkbox";
import Modal from "../../../utils/customModal";
import useApi from "../../../hooks/useApi";
import { errorContainer, loadingHtml } from "../../../utils/functions";

const UserNotifications = ({
  businessId,
  id: institutionId,
  country,
  goBack,
  handleUserDetail,
}) => {
  const [data, setData] = useState([]);
  const [errors, setErrors] = useState(new Set());
  const [modal, setModal] = useState(defaultModal);

  const initialData = useRef();

  const { t } = useTranslation();
  const { getApiCAll, postApiCAll } = useApi();

  const errorMsg = t("general.modal", {
    returnObjects: true,
  });

  //2830
  const getData = async () => {
    try {
      setModal({ content: loadingHtml(), show: true });
      const response = await getApiCAll(
        `/users-notification/institution/${institutionId}`,
        undefined,
        defaultParams
      );
      const data = response.data.data?.content.map(
        ({
          id: userId,
          codUser,
          firstName,
          typeNotificationEmailLisWrapper,
        }) => ({
          userId,
          firstName,
          codUser,
          notifications: typeNotificationEmailLisWrapper.reduce(
            (acc, { id, isContains, nameTypeNotificationEmailTraslade }) => {
              acc[id] = {
                value: isContains,
                text: nameTypeNotificationEmailTraslade,
              };
              return acc;
            },
            {}
          ),
        })
      );
      initialData.current = data;
      setData(data);
      closeModal();
    } catch (error) {
      setDefaultErrorModal();
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleChange = (e) => {
    const { id: tagId, checked } = e.target;
    const [userId, notificationId] = tagId.split("-");

    setData(
      data.map((user) =>
        user.userId !== Number(userId)
          ? user
          : {
              ...user,
              notifications: {
                ...user.notifications,
                [notificationId]: {
                  value: checked,
                  text: user.notifications[notificationId].text,
                },
              },
            }
      )
    );
  };

  const getTableData = () => {
    return data.map(({ userId, codUser, firstName, notifications }) => ({
      userData: (
        <>
          {firstName} <br />{" "}
          <a className="lnk" onClick={() => handleUserDetail(codUser, true)}>
            {codUser}
          </a>
        </>
      ),
      notifications: Object.keys(notifications).reduce((acc, curr) => {
        const { value, text } = notifications[curr];
        acc[curr] = {
          content: (
            <Checkbox
              id={`${userId}-${curr}`}
              checked={value}
              onChange={handleChange}
            />
          ),
          text,
        };
        return acc;
      }, {}),
    }));
  };

  const getHeaders = ({ notifications }) => {
    return [
      {
        center: true,
        selector: "userData",
        name: t("general.headers.notifications"),
        style: { minWidth: 170 },
      },
      ...Object.keys(notifications).map((item) => ({
        style: { minWidth: 180 },
        center: true,
        selector: `notifications.${item}.content`,
        name: renderHeader(+item, notifications[item].text),
      })),
    ];
  };

  const renderHeader = (id, text) => {
    return errors.has(id) ? (
      <ErrorMessage error={text} className="flex-column" />
    ) : (
      text
    );
  };

  const renderComponent = () => {
    if (data.length) {
      const dataTable = getTableData();
      return (
        <>
          <div className="header-title flex-element">
            <div className="title gest-client flex-element flex-column align-items-start">
              <div className="client-gest">
                {t("clients.userNotificationsTitle")}
              </div>
              <br />
              {errors.size > 0 && (
                <ErrorMessage error="Se debe seleccionar almenos un usuario para cada notificación." />
              )}
            </div>
          </div>
          <Table columns={getHeaders(dataTable[0])} data={dataTable} />
          {getActions()}
        </>
      );
    }

    return null;
  };

  const dataToSend = formatData(
    initialData.current,
    data,
    businessId,
    country.code
  );

  const handleSubmit = async () => {
    const notificationMsgs = t("general.modal.notifications", {
      returnObjects: true,
    });
    setModal({ content: loadingHtml(), show: true });

    try {
      await postApiCAll(`/users-notification`, dataToSend);
      setModal({
        show: true,
        content: errorContainer(
          notificationMsgs.updated,
          notificationMsgs.updatedDescription,
          {
            continueBtn: {
              text: t(`general.buttons.continue`),
              action: goBack,
            },
          }
        ),
      });
    } catch (err) {
      const errorHeaders = Object.keys(data[0].notifications).reduce(
        (acc, current) => {
          const active = data.some((item) => item.notifications[current].value);

          if (!active) acc.push(+current);
          return acc;
        },
        []
      );

      setErrors(new Set(errorHeaders));
      setDefaultErrorModal(err?.response?.data?.notifications[0]?.description);
    }
  };

  const getActions = () => {
    return (
      <div className="form-actions clearfix">
        <SecondaryButton id={`send-invitation`} onClick={goBack}>
          {t("general.buttons.back")}
        </SecondaryButton>
        <SecondaryButton
          id={`send-invitation`}
          disabled={!dataToSend.length}
          onClick={handleSubmit}
        >
          {t("general.buttons.apply")}
        </SecondaryButton>
      </div>
    );
  };

  const closeModal = () => setModal(defaultModal);

  const setDefaultErrorModal = (err) => {
    const error = err || errorMsg.serverError;
    setModal({
      show: true,
      content: errorContainer(errorMsg.error, error, {
        continueBtn: {
          text: t(`general.buttons.continue`),
          action: closeModal,
        },
      }),
    });
  };

  return (
    <>
      {renderComponent()}
      <Modal handleClose={closeModal} show={modal.show}>
        {modal.content}
      </Modal>
    </>
  );
};

export default UserNotifications;

const defaultModal = { show: false, content: null };

const defaultParams = {
  search: "user.status/not/DELETED",
  sort: "updateDate,DESC",
};

const formatData = (initialData = [], data = [], businessId, country) => {
  const getNotifications = (items) => {
    return items.flatMap(({ userId, codUser, notifications }) => ({
      data: {
        id: userId,
        codUser,
        businessIdUserNotificationEmail: businessId,
        countryUserNotificationEmail: country,
        notificationEmailList: Object.keys(notifications).flatMap((item) =>
          notifications[item].value ? { id: Number(item) } : []
        ),
      },
    }));
  };

  const initial = getNotifications(initialData);
  const newData = getNotifications(data);

  return newData.reduce((acc, current, index) => {
    if (
      JSON.stringify(current.data.notificationEmailList) !==
      JSON.stringify(initial[index].data.notificationEmailList)
    )
      acc.push(current);

    return acc;
  }, []);
};
